/**
 * Contacts section — top-level navigation, beside Clients (contacts §7.1;
 * handoff §2.1). List view (search + filters + badges + archived toggle +
 * pending-disposition badge) and the detail/edit form.
 *
 * Backend = the C1 gateway methods; until `[M:contacts-C1-api]` lands the
 * page degrades gracefully (method-not-found → pending-backend banner, and
 * the list may show only the owner row from the thin C0 slice).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useLexEventStream } from "@/hooks/useEventStream";
import { filterContacts, isPendingDisposition, TIER_META } from "@/lib/contacts-logic";
import type { Contact, TrustTier } from "@/types/contacts";
import { RELATIONSHIP_SEEDS, OWNER_CONTACT_ID } from "@/types/contacts";
import { ContactDetailForm } from "@/components/contacts/ContactDetailForm";

/** True when the runtime doesn't expose the C1 methods yet. */
export function isMethodNotFound(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /method|unknown|not.?found|unsupported/i.test(msg);
}

export default function ContactsLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contactId } = useParams<{ contactId?: string }>();
  const [searchParams] = useSearchParams();
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);

  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [backendPending, setBackendPending] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const [relationship, setRelationship] = useState<string>("");
  const [tier, setTier] = useState<TrustTier | "">("");
  const [showArchived, setShowArchived] = useState(false);

  const refresh = useCallback(() => {
    if (!rpc || connectionState !== "connected") return;
    rpc
      .contactsList({ includeArchived: true })
      .then((res) => {
        setContacts(res.contacts);
        setBackendPending(false);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (isMethodNotFound(err)) {
          setBackendPending(true);
          setContacts([]);
        } else {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      });
  }, [rpc, connectionState]);

  useEffect(() => refresh(), [refresh]);
  // RT-1 refresh loop: concierge-side writes broadcast contact.updated.
  useLexEventStream("contact.updated", () => refresh());

  const visible = useMemo(
    () =>
      filterContacts(contacts ?? [], {
        query,
        relationship: relationship || undefined,
        trustTier: (tier || undefined) as TrustTier | undefined,
        includeArchived: showArchived,
      }),
    [contacts, query, relationship, tier, showArchived],
  );

  const selected = contactId ? (contacts ?? []).find((c) => c.id === contactId) : undefined;

  return (
    <div className="flex h-full w-full overflow-hidden" data-testid="contacts_layout">
      {/* ── list column ── */}
      <div className="flex w-96 shrink-0 flex-col border-r border-slate-200 dark:border-slate-800">
        <div className="space-y-2 border-b border-slate-200 p-3 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold">{t("contacts.title", "Contatos")}</h1>
            <button
              type="button"
              className="rounded bg-amber-600 px-3 py-1 text-sm font-medium text-white hover:bg-amber-500"
              onClick={() => navigate("/contacts/new")}
              data-testid="contacts_new"
            >
              {t("contacts.new", "Novo contato")}
            </button>
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("contacts.search", "Buscar por nome, e-mail, telefone…")}
            className="w-full rounded border border-slate-300 bg-transparent px-2 py-1 text-sm dark:border-slate-700"
            data-testid="contacts_search"
          />
          <div className="flex gap-2 text-xs">
            <select
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              className="flex-1 rounded border border-slate-300 bg-transparent px-1 py-1 dark:border-slate-700"
              data-testid="contacts_filter_relationship"
            >
              <option value="">{t("contacts.allRelationships", "Todas as relações")}</option>
              {RELATIONSHIP_SEEDS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value as TrustTier | "")}
              className="flex-1 rounded border border-slate-300 bg-transparent px-1 py-1 dark:border-slate-700"
              data-testid="contacts_filter_tier"
            >
              <option value="">{t("contacts.allTiers", "Todos os níveis")}</option>
              {(Object.keys(TIER_META) as TrustTier[]).map((tt) => (
                <option key={tt} value={tt}>
                  {TIER_META[tt].label}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="contacts_toggle_archived"
            />
            {t("contacts.showArchived", "Mostrar arquivados")}
          </label>
        </div>

        {backendPending && (
          <div
            className="m-3 rounded border border-amber-400/50 bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
            data-testid="contacts_backend_pending"
          >
            {t(
              "contacts.backendPending",
              "O diretório de contatos ainda está sendo ativado no seu runtime — as edições ficarão disponíveis em breve.",
            )}
          </div>
        )}
        {loadError && (
          <div className="m-3 rounded border border-red-400/50 bg-red-50 p-2 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-200">
            {loadError}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="contacts_list">
          {contacts === null && !backendPending ? (
            <div className="p-4 text-sm text-slate-500">{t("common.loading", "Carregando…")}</div>
          ) : visible.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">
              {t("contacts.empty", "Nenhum contato ainda.")}
            </div>
          ) : (
            visible.map((c) => {
              const tierMeta = TIER_META[c.trustTier];
              const isOwner = c.id === OWNER_CONTACT_ID;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => navigate(`/contacts/${encodeURIComponent(c.id)}`)}
                  className={`block w-full border-b border-slate-100 px-3 py-2 text-left hover:bg-slate-50 dark:border-slate-800/60 dark:hover:bg-slate-900 ${
                    c.id === contactId ? "bg-slate-100 dark:bg-slate-900" : ""
                  }`}
                  data-testid={`contact_row_${c.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">
                      {c.displayName}
                      {isOwner && (
                        <span className="ml-1 text-xs text-slate-400">
                          {t("contacts.youBadge", "(você)")}
                        </span>
                      )}
                    </span>
                    {isPendingDisposition(c) && (
                      <span
                        className="rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-orange-600 dark:text-orange-400"
                        data-testid="contact_pending_badge"
                      >
                        {t("contacts.pendingBadge", "aguardando decisão")}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                    <span className="rounded bg-slate-500/10 px-1.5 py-0.5">{tierMeta.label}</span>
                    <span className="rounded bg-slate-500/10 px-1.5 py-0.5">{c.relationship}</span>
                    {c.status === "archived" && (
                      <span className="rounded bg-slate-500/20 px-1.5 py-0.5">
                        {t("contacts.archivedBadge", "arquivado")}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ── detail column ── */}
      <div className="min-w-0 flex-1 overflow-y-auto p-4">
        {contactId ? (
          <ContactDetailForm
            key={contactId}
            contactId={contactId === "new" ? undefined : contactId}
            existing={selected}
            backendPending={backendPending}
            onSaved={(saved) => {
              refresh();
              navigate(`/contacts/${encodeURIComponent(saved.id)}`);
            }}
            onArchivedChange={() => refresh()}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            {t("contacts.selectHint", "Selecione um contato — ou crie um novo.")}
          </div>
        )}
      </div>
    </div>
  );
}
